---
title: Fader
description: A linear slider for gain and other continuous parameters.
component: true
base: base
---

<ComponentPreview styleName="base-nova" name="fader-vertical-demo" />

A linear control for continuous parameters such as volume, gain or a send level. The registry component wraps the `Fader` primitive from `@audio-ui/react` and adds size variants and thumb marks.

## Overview

- **Input:** drag the thumb or press the track, or use the mouse wheel or the keyboard. `onValueCommitted` fires when the interaction ends.
- **Layout:** vertical by default, or horizontal, in `sm`, `default` and `lg` sizes.
- **Display:** a filled range and a thumb, with optional marks inside the thumb (`thumbMarks`).
- **Accessibility:** the thumb holds a native `<input type="range">` that carries the accessible name (`aria-label` or `aria-labelledby`), the value text (formatted with `format`) and the form value (`name`).

## Installation

<Install name="fader" packages="@audio-ui/react class-variance-authority" />

## Anatomy

Import the primitive and assemble its parts.

```tsx
import { Fader } from "@audio-ui/react";

<Fader.Root>
  <Fader.Label />
  <Fader.Control>
    <Fader.Track>
      <Fader.Indicator />
    </Fader.Track>
    <Fader.Mark value={50} />
    <Fader.Thumb />
  </Fader.Control>
  <Fader.Value />
</Fader.Root>
```

## Usage

```tsx
import { Fader } from "@/components/audio-ui/fader";
```

**Uncontrolled:** set only the initial value.

```tsx
<Fader defaultValue={0} max={6} min={-60} step={1} />
```

**Controlled:** drive the value from state.

```tsx
const [gain, setGain] = useState(0);

<Fader
  max={6}
  min={-60}
  onValueChange={setGain}
  onValueCommitted={(value, details) => console.log("Committed:", value, details.reason)}
  step={1}
  value={gain}
/>
```

## Interaction

### Pointer

<KeyTable title="Behavior">
<KeyRow gesture={"Track click / drag"}>Click or drag on the track to set the value from the pointer position.</KeyRow>
<KeyRow gesture={"Thumb drag"}>Dragging the thumb changes the value continuously, within `min…max`.</KeyRow>
<KeyRow gesture={"Commit event"}>`onValueCommitted` fires when an interaction ends with a new value.</KeyRow>
<KeyRow gesture={"Lagging value"}>While dragging, the thumb follows the pointer even if a controlled `value` updates late; it returns to `value` on release. Reject a value with `eventDetails.cancel()`.</KeyRow>
</KeyTable>

### Keyboard

On the focused thumb:

<KeyTable title="Keys">
<KeyRow gesture={"`ArrowUp` / `ArrowRight`"}>Increase by `step`</KeyRow>
<KeyRow gesture={"`ArrowDown` / `ArrowLeft`"}>Decrease by `step`</KeyRow>
<KeyRow gesture={"`PageUp` / `Shift` + `ArrowUp`"}>Increase by `largeStep` (default `10`)</KeyRow>
<KeyRow gesture={"`PageDown` / `Shift` + `ArrowDown`"}>Decrease by `largeStep` (default `10`)</KeyRow>
<KeyRow gesture={"`Home`"}>Set to `min`</KeyRow>
<KeyRow gesture={"`End`"}>Set to `max`</KeyRow>
</KeyTable>

### Wheel

The mouse wheel over the fader moves the value by `step` and reports `eventDetails.reason === "wheel"`.

## API reference

### Root

Groups all parts of the fader, with audio defaults (vertical, -60…+6) and mouse-wheel stepping. While a pointer drags it, the thumb follows the pointer even if a controlled `value` lags behind, as with a seek bar driven by playback time or automation. To reject a value, call `eventDetails.cancel()`. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"anchor"} type={"number"}>Value the `Indicator` fills from, such as `0` on a -1…1 pan. Defaults to `min`.</TypeRow>
<TypeRow name={"defaultValue"} type={"number"}>Uncontrolled starting value, and what a double click, an Alt + click or the Delete key return to; defaults to the midpoint of `min…max`.</TypeRow>
<TypeRow name={"max"} type={"number"} default={"6"}></TypeRow>
<TypeRow name={"min"} type={"number"} default={"-60"}></TypeRow>
<TypeRow name={"onValueChange"} type={"(value: number, eventDetails: ChangeEventDetails) => void"}>Fires on drag, track press, keyboard, wheel and form input changes.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: number, eventDetails: CommitEventDetails) => void"}>Fires when an interaction ends with a new value.</TypeRow>
<TypeRow name={"orientation"} type={"\"horizontal\" | \"vertical\""} default={"\"vertical\""}></TypeRow>
<TypeRow name={"value"} type={"number"}></TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-active"}>Whether the value has reached this mark from the anchor.</TypeRow>
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
</TypeTable>

### Control

The interactive area: pointer presses and drags, plus mouse-wheel stepping. Sets `touch-action: none` so touch drags don't scroll the page. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-active"}>Whether the value has reached this mark from the anchor.</TypeRow>
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
</TypeTable>

### Indicator

The filled part of the track: from `min` up to the value, or between the Root's `anchor` and the value, on whichever side of it the value is. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-active"}>Whether the value has reached this mark from the anchor.</TypeRow>
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
</TypeTable>

### Mark

A graduation along the track at `value`: placed from the start of the travel in the fader's direction, where the thumb's center would be at that value. It carries `data-active` while the value has reached it from the anchor, and `data-orientation`. Decorative: the thumb's input announces the value. Render your own tick and label inside it. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"value"} type={"number"}>Where the mark sits, between `min` and `max`.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-active"}>Whether the value has reached this mark from the anchor.</TypeRow>
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
</TypeTable>

### Registry component

`Fader` accepts style props (`size`, `thumbMarks`, `className`) plus primitive props from `FaderPrimitive.Root.Props` (`name`, `form`, `format`, `locale`, `largeStep`, `render`, …). The primitive's `Fader.Mark` places a graduation at a `value` along the track, with `data-active` once the value has reached it, to build your own ruler. `aria-label`, `aria-labelledby`, and `aria-describedby` are forwarded to the thumb's input.

#### Value and state

<TypeTable>
<TypeRow name={"value"} type={"number"}>Where the mark sits, between `min` and `max`.</TypeRow>
<TypeRow name={"defaultValue"} type={"number"} default={"midpoint"}>Uncontrolled starting value, and what a double click, an Alt + click or the Delete key return to; defaults to the midpoint of `min…max`.</TypeRow>
<TypeRow name={"min"} type={"number"} default={"-60"}>Minimum value.</TypeRow>
<TypeRow name={"max"} type={"number"} default={"6"}>Maximum value.</TypeRow>
<TypeRow name={"step"} type={"number"} default={"1"}>Granularity of every change.</TypeRow>
<TypeRow name={"marks"} type={"(number | { value, label? })[]"}>Graduations drawn beside the track: a ruler for the value. Those the value has reached from `anchor` light up.</TypeRow>
<TypeRow name={"valueLabel"} type={"\"auto\" | \"always\" | \"hidden\""} default={"\"auto\""}>When the value shows beside the thumb: `auto` while hovered, dragged or focused from the keyboard.</TypeRow>
<TypeRow name={"formatValue"} type={"(value: number) => string"} default={"format"}>How the value prints in that readout and in what screen readers announce, e.g. `"-6.0 dB"`.</TypeRow>
<TypeRow name={"editableValue"} type={"boolean"} default={"false"}>Lets you type the value in the readout: a double click, or Enter while it has focus. Enter commits it, Escape drops it (`reason: "input-change"`).</TypeRow>
<TypeRow name={"anchor"} type={"number"} default={"min"}>Value the `Indicator` fills from, such as `0` on a -1…1 pan. Defaults to `min`.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"} default={"false"}>Disables interaction.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value: number, eventDetails) => void"}>Fires on drag, track press, keyboard, wheel and form input changes.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: number, eventDetails) => void"}>Fires when an interaction ends with a new value.</TypeRow>
<TypeRow name={"aria-label"} type={"string"}>Accessible name for the slider.</TypeRow>
<TypeRow name={"aria-labelledby"} type={"string"}>ID of a labeling element.</TypeRow>
</TypeTable>

#### Layout and visuals

<TypeTable>
<TypeRow name={"orientation"} type={"\"horizontal\" | \"vertical\""} default={"\"vertical\""}>Direction of travel.</TypeRow>
<TypeRow name={"size"} type={"\"sm\" | \"default\" | \"lg\""} default={"\"default\""}>Track and thumb size preset.</TypeRow>
<TypeRow name={"thumbMarks"} type={"number | false"} default={"3"}>Number of marks in the thumb or `false` to hide them.</TypeRow>
<TypeRow name={"className"} type={"string"}>Root wrapper class name.</TypeRow>
</TypeTable>

Additional valid HTML attributes passed to `Fader.Root` are forwarded.

A vertical fader takes the height it is given, and never less than `--fader-min-height` (10rem by default). Set it lower where room is short, such as a mixer on a small screen: `[--fader-min-height:5rem]` on a parent.

## Examples

### Vertical (default)

<ComponentPreview styleName="base-nova" name="fader-vertical-demo" />

### Horizontal

<ComponentPreview styleName="base-nova" name="fader-horizontal-demo" />

### Pan, filled from the center

`anchor={0}` makes the fill grow from the center of a -1…1 range, left or right of it.

<ComponentPreview styleName="base-nova" name="fader-pan-demo" />

### Mixing console

A fader per channel in dB, from silent at the bottom to +6 dB, beside the meter of what it lets through.

<ComponentPreview styleName="base-nova" name="block-mixer" />

